import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, ViewChild } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { ActivatedRoute, Router } from '@angular/router'
import { debounceTime, distinctUntilChanged, filter, map, pairwise, startWith, Subject, Subscription } from 'rxjs'
import { MatSliderModule } from '@angular/material/slider'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateModule } from '@ngx-translate/core'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { UsersService } from '../users.service'
import { CallsService, PauseInterval, MetricType } from '../calls.service'
import { MatMenu, MatMenuTrigger } from '@angular/material/menu'
import { MetricsService } from '../metrics.service'

export function useMediaTimePrecision(mediaTime: number): number {
  // Make the number rational as required by the media player
  return parseFloat(mediaTime.toFixed(2))
}

type ControlName = 'VOLUME' | 'DOWNLOAD' | 'PLAYBACK_RATE' | 'NEXT_SPEAKER' | 'MUTE' | 'MISC' | 'SKIP_30_SECONDS'
type ControlDefinition = {
  name: ControlName
  width: number
}
const controls: { [control: string]: ControlDefinition } = {
  VOLUME: { name: 'VOLUME', width: 50 },
  DOWNLOAD: { name: 'DOWNLOAD', width: 50 },
  PLAYBACK_RATE: { name: 'PLAYBACK_RATE', width: 50 },
  NEXT_SPEAKER: { name: 'NEXT_SPEAKER', width: 20 },
  SKIP_30_SECONDS: { name: 'SKIP_30_SECONDS', width: 40 },
  MUTE: { name: 'MUTE', width: 50 },
  MISC: { name: 'MISC', width: 50 },
}

const controlPriority = ['MISC', 'MUTE', 'NEXT_SPEAKER', 'PLAYBACK_RATE', 'DOWNLOAD', 'VOLUME', 'SKIP_30_SECONDS']

@Component({
  selector: 'app-media-player-controller',
  standalone: true,
  imports: [MatSliderModule, MatIcon, MatIconButton, MatTooltip, TranslateModule, MatIconButtonSizesModule, MatMenu, MatMenuTrigger],
  templateUrl: './media-player-controller.component.html',
  styleUrl: './media-player-controller.component.scss',
})
export class MediaPlayerControllerComponent implements OnDestroy, AfterViewInit {
  @Input({ required: true })
  currentTime!: number

  @Input({ required: true })
  duration!: number

  @Input({ required: true })
  isPlaying!: boolean

  @Input()
  displayFullscreenButton: boolean = true

  @Input({ required: true })
  allowDownload!: boolean

  @Input()
  displaySpeakerLabelingModeButton: boolean = false

  @Input({ required: true })
  pauseIntervals!: PauseInterval[]

  @Output()
  play = new EventEmitter<void>()

  @Output()
  pause = new EventEmitter<void>()

  @Output()
  seek = new EventEmitter<number>()

  @Output()
  changePlaybackRate = new EventEmitter<number>()

  @Output()
  jumpToNextSpeaker = new EventEmitter<void>()

  @Output()
  changeVolume = new EventEmitter<number>()

  @Output()
  toggleFullscreen = new EventEmitter<void>()

  @Output()
  download = new EventEmitter<void>()

  @ViewChild('controlBar', { static: false })
  controlBar!: ElementRef<HTMLTextAreaElement>

  router = inject(Router)
  route = inject(ActivatedRoute)
  usersService = inject(UsersService)
  callsService = inject(CallsService)
  metricsService = inject(MetricsService)

  playbackRate = 1
  volume = 1
  currentUser$ = this.usersService.currentUser$
  isMuted = false

  private lastVolume = 1
  private trackMediaTimeChangesSubscription: Subscription | null = null
  private seekSubject = new Subject<number>()

  controls = controls
  private currentWidth!: number

  ngAfterViewInit(): void {
    this.trackMediaTimeChanges()
    this.setupSeekThrottler()

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === this.controlBar.nativeElement) {
          const { width } = entry.contentRect
          this.currentWidth = width
        }
      }
    })
    resizeObserver.observe(this.controlBar.nativeElement)

    this.route.queryParams
      .pipe(
        map((params) => params['speakerLabelingModeEnabled'] === 'true'),
        distinctUntilChanged(),
        pairwise()
      )
      .subscribe(([wasEnabled, isEnabled]) => {
        if (wasEnabled && !isEnabled) {
          this.pause.emit()
        }
      })
  }

  get pauseOffsets() {
    return this.pauseIntervals.filter((pauseInterval) => pauseInterval.start < this.duration).map((pauseInterval) => pauseInterval.start)
  }
  get activeControls() {
    let width = 350
    return controlPriority
      .map((controlName) => controls[controlName]!)
      .reduce((acc, control) => {
        width += control.width
        if (this.currentWidth > width) {
          return [...acc, control.name]
        }
        return acc
      }, [] as ControlName[])
  }

  isActive(controlName: ControlName): boolean {
    return this.activeControls.includes(controlName)
  }

  get displayMenuButton(): boolean {
    return this.activeControls.length !== controlPriority.length
  }

  ngOnDestroy(): void {
    this.trackMediaTimeChangesSubscription?.unsubscribe()
    this.seekSubject.complete()
  }

  togglePlay() {
    if (this.isPlaying) {
      this.pause.emit()
    } else {
      this.play.emit()
    }
  }

  toggleSpeakerLabelingMode() {
    const value = this.isSpeakerLabelingModeEnabled ? null : true
    this.router.navigate([], {
      queryParams: { speakerLabelingModeEnabled: value },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
    const callId = parseInt(this.route.snapshot.params['id'])
    if (value === true) {
      this.metricsService.sendMetric(callId, MetricType.ENABLE_SPEAKER_LABELING_MODE)
    } else {
      this.metricsService.sendMetric(callId, MetricType.DISABLE_SPEAKER_LABELING_MODE, { completed: false })
    }
  }

  get isSpeakerLabelingModeEnabled(): boolean {
    return this.route.snapshot.queryParams['speakerLabelingModeEnabled'] === 'true'
  }

  get isShowingSpeakers(): boolean {
    return this.route.snapshot.queryParams['showSpeakers'] === 'true' && !this.isSpeakerLabelingModeEnabled
  }

  handleJumpToNextSpeaker() {
    this.jumpToNextSpeaker.emit()
  }

  handleSkipSeconds(seconds: number) {
    const newTime = this.currentTime + seconds
    const clampedTime = Math.max(0, Math.min(newTime, this.duration))
    this.handleSeek(clampedTime)
  }

  handleShowSpeakers() {
    const displayingSpeakers = this.route.snapshot.queryParams['showSpeakers'] === 'true'
    this.router.navigate([], {
      queryParams: { showSpeakers: displayingSpeakers ? null : 'true' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
    this.metricsService.sendMetric(parseInt(this.route.snapshot.params['id']), MetricType.CLICK_ON_SPEAKERS_XRAY_BUTTON)
  }

  handleSeek(time: number) {
    this.seek.emit(time)
  }

  toggleMute() {
    if (this.isMuted) {
      this.volume = this.lastVolume
      this.isMuted = false
    } else {
      this.lastVolume = this.volume > 0 ? this.volume : 1
      this.volume = 0
      this.isMuted = true
    }
    this.changeVolume.emit(this.volume)
  }

  private trackMediaTimeChanges() {
    this.trackMediaTimeChangesSubscription = this.route.queryParams
      .pipe(
        filter((params) => !this.isPlaying || params['forceMediaTimeUpdate']),
        map((params) => ({ mediaTime: params['mediaTime'] ?? null, startMedia: params['startMedia'] === 'true' })),
        filter((params) => params.mediaTime !== null),
        distinctUntilChanged((previous, current) => previous.mediaTime === current.mediaTime),
        map((params) => ({ time: useMediaTimePrecision(parseFloat(params.mediaTime)), startMedia: params.startMedia }))
      )
      .subscribe((params) => {
        this.handleSeek(params.time)

        this.router.navigate([], {
          queryParams: { startMedia: null, forceMediaTimeUpdate: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })

        if (params.startMedia) {
          this.play.emit()
        }
      })
  }

  formatPlayerTime(timeInSeconds: number): string {
    // TODO use a pipe
    const hours = Math.floor(timeInSeconds / 3600)
    const minutes = Math.floor((timeInSeconds % 3600) / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    const formattedTime = `${hours > 0 ? hours + ':' : ''}${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    return formattedTime
  }

  onSliderChange(event: any) {
    this.seekSubject.next(event.target.value)
  }

  setupSeekThrottler() {
    this.seekSubject.pipe(debounceTime(50), distinctUntilChanged()).subscribe((value) => this.handleSeek(value))
  }

  changePlayBackRate() {
    if (this.playbackRate === 1) {
      this.playbackRate = 1.5
    } else if (this.playbackRate === 1.5) {
      this.playbackRate = 2
    } else if (this.playbackRate === 2) {
      this.playbackRate = 0.5
    } else {
      this.playbackRate = 1
    }
    this.changePlaybackRate.emit(this.playbackRate)
  }

  onToggleFullscreen() {
    this.toggleFullscreen.emit()
  }
}
